import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { usePortalSlug } from "@/contexts/PortalDomainContext";
import api from "@/lib/api";
import { loadTenantFont, resolveLogoUrl } from "@/lib/branding";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Recycle, ArrowRight, ArrowLeft, CheckCircle, MapPin, Warning, EnvelopeSimple, HandWaving } from "@phosphor-icons/react";
import usePageTitle from "@/lib/usePageTitle";

const STEPS = ["Adresse", "Tonne", "Plan", "Daten", "Bezahlen"];

export default function BookingFlow() {
  const slug = usePortalSlug();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState(null);
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(0);
  const [packages, setPackages] = useState({});
  usePageTitle("Buchung — " + (STEPS[step] || "Start"), tenant?.name);
  const [data, setData] = useState({
    address: {
      street: "",
      zip: searchParams.get("prefill_zip") || "",
      city: searchParams.get("prefill_city") || "",
    },
    geo: null,
    bin_type: "restmuell",
    size: 120,
    package_id: "abo_quarterly_120",
    customer: { type: "private", first_name: "", last_name: "", email: "", phone: "", company_name: "", contact_person: "", is_returning: false },
    zone: null,
    next_pickup_date: null,
  });
  const [checking, setChecking] = useState(false);
  const [paying, setPaying] = useState(false);
  const [interestState, setInterestState] = useState(null); // null | {reason, matched_zip, form, submitting, submitted}

  useEffect(() => {
    api.get(`/tenants/public/${slug}`).then((r) => {
      setTenant(r.data);
      const b = r.data.branding || {};
      document.documentElement.style.setProperty("--tenant-primary", b.primary_color || "#2563EB");
      document.documentElement.style.setProperty("--tenant-secondary", b.secondary_color || "#0EA5E9");
      document.documentElement.style.setProperty("--tenant-font", `"${b.font_family || "Outfit"}", sans-serif`);
      loadTenantFont(b.font_family);
    });
    api.get("/checkout/packages").then((r) => setPackages(r.data.customer || {}));
  }, [slug]);

  if (!tenant) return <div className="min-h-screen flex items-center justify-center">Lade…</div>;
  const b = tenant.branding || {};
  const eur = (a) => `€${a.toFixed(2)}`;

  const checkAddress = async () => {
    setChecking(true);
    setInterestState(null);
    try {
      // 1) Geocode via Mapbox
      const q = `${data.address.street}, ${data.address.zip} ${data.address.city}`;
      const r = await api.get("/mapbox/geocode", { params: { q } });
      const feat = (r.data.features || [])[0];
      if (!feat) {
        // Adresse nicht auffindbar → Interessent
        setInterestState({ reason: "not_geocoded", form: { first_name: "", last_name: "", email: "", phone: "", message: "" } });
        return;
      }
      const [lng, lat] = feat.geometry.coordinates;
      // 2) Servicegebiet-Check
      const sa = await api.get(`/portal/${slug}/service-area/check`, { params: { zip: data.address.zip, city: data.address.city } });
      if (sa.data.configured && !sa.data.covered) {
        setInterestState({ reason: "out_of_area", form: { first_name: "", last_name: "", email: "", phone: "", message: "" } });
        return;
      }
      // 2b) Zone-Lookup: Aufnahmestopp und erste Reinigung
      let zoneInfo = null;
      try {
        const zr = await api.get(`/portal/${slug}/zone-lookup`, { params: { zip: data.address.zip } });
        zoneInfo = zr.data;
      } catch (e) { console.warn("[BookingFlow] zone lookup failed", e); }
      if (zoneInfo && zoneInfo.signup_stopped) {
        setInterestState({
          reason: "signup_stopped",
          stop_reason: zoneInfo.stop_reason,
          form: { first_name: "", last_name: "", email: "", phone: "", message: zoneInfo.stop_reason ? `Aufnahmestopp: ${zoneInfo.stop_reason}` : "" },
        });
        return;
      }
      // 3) Alles ok → weiter
      setData({
        ...data,
        geo: { lat, lng },
        zone: zoneInfo?.zone || null,
        next_pickup_date: zoneInfo?.next_pickup_date || null,
      });
      setStep(1);
    } catch (e) {
      setInterestState({ reason: "not_geocoded", form: { first_name: "", last_name: "", email: "", phone: "", message: "" } });
    } finally {
      setChecking(false);
    }
  };

  const submitInterest = async () => {
    if (!interestState) return;
    setInterestState({ ...interestState, submitting: true });
    try {
      await api.post(`/portal/${slug}/leads`, {
        ...interestState.form,
        street: data.address.street,
        zip: data.address.zip,
        city: data.address.city,
        reason: interestState.reason,
      });
      setInterestState({ ...interestState, submitting: false, submitted: true });
    } catch (e) {
      setInterestState({ ...interestState, submitting: false, error: e.response?.data?.detail || "Fehler beim Speichern" });
    }
  };

  const pay = async () => {
    setPaying(true);
    try {
      const origin = window.location.origin;
      const r = await api.post("/checkout/session", {
        package_id: data.package_id,
        origin_url: origin,
        metadata: {
          tenant_id: tenant.tenant_id,
          tenant_slug: slug,
          customer_email: data.customer.email,
          customer_type: data.customer.type || "private",
          customer_name: data.customer.type === "business" && data.customer.company_name
            ? data.customer.company_name
            : `${data.customer.first_name} ${data.customer.last_name}`,
          contact_person: data.customer.contact_person || "",
          is_returning: data.customer.is_returning ? "1" : "0",
          starter_package_via: data.customer.is_returning ? "email" : "post",
          first_pickup_date: data.next_pickup_date || "",
          zone_id: data.zone?.zone_id || "",
          zone_name: data.zone?.name || "",
          address: `${data.address.street}, ${data.address.zip} ${data.address.city}`,
          bin_type: data.bin_type,
          size: String(data.size),
        },
      });
      window.location.href = r.data.url;
    } catch (e) {
      alert("Checkout-Fehler: " + (e.response?.data?.detail || e.message));
      setPaying(false);
    }
  };

  if (step === 0) return (
    <BookingWrap slug={slug} tenant={tenant} branding={b} step={step}>
      {interestState ? (
        <InterestPanel
          state={interestState}
          setState={setInterestState}
          onSubmit={submitInterest}
          onBack={() => setInterestState(null)}
          address={data.address}
          tenant={tenant}
          primary={b.primary_color}
        />
      ) : (
        <Card title="Wo sollen wir reinigen?" sub="Wir prüfen, ob wir in Ihrer Straße aktiv sind.">
          <div className="space-y-3">
            <div><Label className="text-xs">Straße & Hausnummer</Label><Input className="h-12 rounded-md" placeholder="Musterstr. 12" value={data.address.street} onChange={(e) => setData({ ...data, address: { ...data.address, street: e.target.value } })} data-testid="book-street" /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">PLZ</Label><Input className="h-12 rounded-md" placeholder="10115" value={data.address.zip} onChange={(e) => setData({ ...data, address: { ...data.address, zip: e.target.value } })} data-testid="book-zip" /></div>
              <div className="col-span-2"><Label className="text-xs">Stadt</Label><Input className="h-12 rounded-md" placeholder="Berlin" value={data.address.city} onChange={(e) => setData({ ...data, address: { ...data.address, city: e.target.value } })} data-testid="book-city" /></div>
            </div>
          </div>
          <Footer onNext={checkAddress} nextLabel={checking ? "Prüfe…" : "Adresse prüfen"} nextDisabled={!data.address.street || !data.address.zip || checking} testIdNext="book-next-address" />
        </Card>
      )}
    </BookingWrap>
  );

  if (step === 1) return (
    <BookingWrap slug={slug} tenant={tenant} branding={b} step={step}>
      <Card title="Welche Tonne reinigen wir?" sub="Wählen Sie Typ und Größe.">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {[
            { k: "restmuell", l: "Restmüll", c: "#52525B" },
            { k: "biomuell", l: "Biotonne (braun)", c: "#8B5A2B" },
            { k: "papier", l: "Papier", c: "#2563EB" },
            { k: "gelb", l: "Gelb (Plastik)", c: "#FACC15" },
          ].map((t) => (
            <button key={t.k} onClick={() => setData({ ...data, bin_type: t.k })} className={`p-4 border-2 rounded-md text-left transition ${data.bin_type === t.k ? "" : "border-black/10 opacity-60 hover:opacity-100"}`} style={{ borderColor: data.bin_type === t.k ? b.primary_color : undefined }} data-testid={`bin-${t.k}`}>
              <div className="w-8 h-10 rounded-sm mb-2" style={{ background: t.c }} />
              <div className="text-sm font-medium">{t.l}</div>
            </button>
          ))}
        </div>
        <Label className="text-xs">Größe</Label>
        <div className="grid grid-cols-3 gap-3 mt-2">
          {[120, 240, 1100].map((s) => (
            <button key={s} onClick={() => setData({ ...data, size: s, package_id: s === 240 ? "abo_quarterly_240" : "abo_quarterly_120" })} className={`p-4 border-2 rounded-md transition ${data.size === s ? "" : "border-black/10 opacity-60 hover:opacity-100"}`} style={{ borderColor: data.size === s ? b.primary_color : undefined }} data-testid={`size-${s}`}>
              <div className="font-mono text-2xl font-medium">{s}L</div>
            </button>
          ))}
        </div>
        <Footer onBack={() => setStep(0)} onNext={() => setStep(2)} testIdNext="book-next-bin" />
      </Card>
    </BookingWrap>
  );

  if (step === 2) return (
    <BookingWrap slug={slug} tenant={tenant} branding={b} step={step}>
      <Card title="Welcher Plan passt?" sub="Einmalig oder dauerhaft sauber — Sie entscheiden.">
        <div className="space-y-3">
          {Object.entries(packages).map(([key, p]) => (
            <button key={key} onClick={() => setData({ ...data, package_id: key })} className={`w-full p-5 border-2 rounded-md text-left transition flex items-center justify-between ${data.package_id === key ? "" : "border-black/10 opacity-70 hover:opacity-100"}`} style={{ borderColor: data.package_id === key ? b.primary_color : undefined }} data-testid={`plan-${key}`}>
              <div>
                <div className="font-semibold">{p.name}</div>
                <div className="text-xs text-zinc-500 mt-1">{key.startsWith("abo_") ? "Wiederkehrendes Abo" : "Einmalige Reinigung"}</div>
              </div>
              <div className="font-mono text-2xl font-bold" style={{ color: data.package_id === key ? b.primary_color : "#000" }}>{eur(p.amount)}</div>
            </button>
          ))}
        </div>
        <Footer onBack={() => setStep(1)} onNext={() => setStep(3)} testIdNext="book-next-plan" />
      </Card>
    </BookingWrap>
  );

  if (step === 3) return (
    <BookingWrap slug={slug} tenant={tenant} branding={b} step={step}>
      <Card title="Ihre Kontaktdaten" sub="Wir senden Ihnen Termin-Erinnerungen und die Rechnung.">
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label className="text-xs">Anmeldung als</Label>
            <div className="flex gap-2 mt-1" role="tablist">
              <button type="button" onClick={() => setData({ ...data, customer: { ...data.customer, type: "private" } })} className={`flex-1 h-11 rounded-md border text-sm font-semibold transition ${data.customer.type === "private" ? "text-white" : "border-black/10 text-zinc-500"}`} style={data.customer.type === "private" ? { background: b.primary_color, borderColor: b.primary_color } : {}} data-testid="book-type-private">Privatperson</button>
              <button type="button" onClick={() => setData({ ...data, customer: { ...data.customer, type: "business" } })} className={`flex-1 h-11 rounded-md border text-sm font-semibold transition ${data.customer.type === "business" ? "text-white" : "border-black/10 text-zinc-500"}`} style={data.customer.type === "business" ? { background: b.primary_color, borderColor: b.primary_color } : {}} data-testid="book-type-business">Gewerbe / Firma</button>
            </div>
          </div>
          {data.customer.type === "business" && (
            <>
              <div className="col-span-2"><Label className="text-xs">Firma</Label><Input className="h-12 rounded-md" value={data.customer.company_name} onChange={(e) => setData({ ...data, customer: { ...data.customer, company_name: e.target.value } })} data-testid="book-company" /></div>
              <div className="col-span-2"><Label className="text-xs">Ansprechpartner (Kontaktperson)</Label><Input className="h-12 rounded-md" value={data.customer.contact_person} onChange={(e) => setData({ ...data, customer: { ...data.customer, contact_person: e.target.value } })} placeholder="z. B. Max Müller (Facility Manager)" data-testid="book-contact-person" /></div>
            </>
          )}
          <div><Label className="text-xs">Vorname</Label><Input className="h-12 rounded-md" value={data.customer.first_name} onChange={(e) => setData({ ...data, customer: { ...data.customer, first_name: e.target.value } })} data-testid="book-first" /></div>
          <div><Label className="text-xs">Nachname</Label><Input className="h-12 rounded-md" value={data.customer.last_name} onChange={(e) => setData({ ...data, customer: { ...data.customer, last_name: e.target.value } })} data-testid="book-last" /></div>
          <div className="col-span-2"><Label className="text-xs">E-Mail</Label><Input className="h-12 rounded-md" type="email" value={data.customer.email} onChange={(e) => setData({ ...data, customer: { ...data.customer, email: e.target.value } })} data-testid="book-email" /></div>
          <div className="col-span-2"><Label className="text-xs">Telefon (optional)</Label><Input className="h-12 rounded-md" value={data.customer.phone} onChange={(e) => setData({ ...data, customer: { ...data.customer, phone: e.target.value } })} data-testid="book-phone" /></div>
          <div className="col-span-2 mt-2 p-3 rounded-md bg-blue-50 border border-blue-200">
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={data.customer.is_returning || false}
                onChange={(e) => setData({ ...data, customer: { ...data.customer, is_returning: e.target.checked } })}
                className="w-5 h-5 mt-0.5 flex-shrink-0"
                data-testid="book-returning-checkbox"
              />
              <div className="text-sm text-blue-900">
                <strong>Ich war schon einmal Kunde bei euch</strong>
                <div className="text-xs text-blue-700 mt-0.5">Dann verzichten wir auf das Starter-Paket per Post — Termine kommen direkt per E-Mail.</div>
              </div>
            </label>
          </div>
        </div>
        <Footer onBack={() => setStep(2)} onNext={() => setStep(4)} nextDisabled={!data.customer.email || !data.customer.first_name || (data.customer.type === "business" && !data.customer.company_name)} testIdNext="book-next-data" />
      </Card>
    </BookingWrap>
  );

  // Step 4 — Pay
  const pkg = packages[data.package_id];
  return (
    <BookingWrap slug={slug} tenant={tenant} branding={b} step={step}>
      <Card title="Zusammenfassung" sub="Prüfen Sie Ihre Buchung und schließen Sie sicher per Stripe ab.">
        <div className="border border-black/10 rounded-md divide-y divide-black/5">
          <Row k="Anbieter" v={tenant.name} />
          <Row k="Adresse" v={`${data.address.street}, ${data.address.zip} ${data.address.city}`} />
          <Row k="Tonne" v={`${data.bin_type} · ${data.size}L`} />
          <Row k="Plan" v={pkg?.name || data.package_id} />
          <Row k="Kunde" v={`${data.customer.first_name} ${data.customer.last_name} · ${data.customer.email}`} />
          {data.next_pickup_date && (
            <Row k="Erste Reinigung" v={`${new Date(data.next_pickup_date).toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}${data.zone ? " · " + data.zone.name : ""}`} />
          )}
          <Row k="Starter-Paket" v={data.customer.is_returning ? "Nicht nötig (Wiederkehrender Kunde) — Termine per E-Mail" : "Wird postalisch versendet"} />
          <Row k="Preis" v={pkg ? eur(pkg.amount) : "—"} highlight />
        </div>
        <Footer onBack={() => setStep(3)} onNext={pay} nextLabel={paying ? "Weiter zu Stripe…" : "Sicher bezahlen"} nextDisabled={paying} testIdNext="book-pay" />
      </Card>
    </BookingWrap>
  );
}

const Card = ({ title, sub, children }) => (
  <div className="bg-white rounded-lg border border-black/5 p-8">
    <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight mb-2">{title}</h2>
    <p className="text-sm text-zinc-500 mb-6">{sub}</p>
    {children}
  </div>
);

const BookingWrap = ({ slug, tenant, branding: b, step, children }) => (
  <div className="min-h-screen bg-tenant-surface font-tenant" style={{ background: "var(--tenant-surface)", fontFamily: "var(--tenant-font)" }}>
    <header className="bg-white border-b border-black/5">
      <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to={`/portal/${slug}`} className="flex items-center gap-2">
          {b.logo_url ? <img src={resolveLogoUrl(b.logo_url)} alt={tenant.name} className="h-7" /> :
            <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ background: b.primary_color }}><Recycle size={14} weight="bold" className="text-white" /></div>}
          <span className="font-bold">{tenant.name}</span>
        </Link>
        <div className="text-xs text-zinc-500">Sichere Buchung</div>
      </div>
    </header>
    <main className="max-w-3xl mx-auto px-6 py-12">
      {/* Stepper */}
      <div className="flex items-center gap-2 mb-10">
        {STEPS.map((s, i) => (
          <React.Fragment key={s}>
            <div className={`flex items-center gap-2 text-xs ${i === step ? "" : "opacity-50"}`}>
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold ${i < step ? "text-white" : i === step ? "text-white" : "bg-zinc-200 text-zinc-500"}`}
                style={{ background: i <= step ? b.primary_color : undefined }}>
                {i < step ? "✓" : i + 1}
              </div>
              <span className="hidden md:inline">{s}</span>
            </div>
            {i < STEPS.length - 1 && <div className="flex-1 h-px bg-black/10" />}
          </React.Fragment>
        ))}
      </div>
      {children}
    </main>
  </div>
);

const Footer = ({ onBack, onNext, nextLabel = "Weiter", nextDisabled, testIdNext }) => (
  <div className="mt-8 flex items-center justify-between">
    <div>{onBack && <Button variant="outline" className="rounded-md" onClick={onBack}><ArrowLeft size={14} weight="bold" className="mr-1.5" /> Zurück</Button>}</div>
    <Button onClick={onNext} disabled={nextDisabled} className="rounded-md text-white h-12 px-7" style={{ background: "var(--tenant-primary)" }} data-testid={testIdNext}>
      {nextLabel} <ArrowRight size={14} weight="bold" className="ml-1.5" />
    </Button>
  </div>
);

const Row = ({ k, v, highlight }) => (
  <div className={`flex items-center justify-between px-4 py-3 ${highlight ? "bg-zinc-50" : ""}`}>
    <span className="text-xs uppercase tracking-widest text-zinc-500">{k}</span>
    <span className={`text-sm ${highlight ? "font-mono font-bold text-lg" : "font-medium"}`}>{v}</span>
  </div>
);


function InterestPanel({ state, setState, onSubmit, onBack, address, tenant, primary }) {
  if (state.submitted) {
    return (
      <div className="bg-white rounded-lg border border-black/5 p-8 md:p-10 text-center" data-testid="interest-thankyou">
        <div className="w-16 h-16 rounded-full mx-auto mb-5 flex items-center justify-center text-white" style={{ background: primary }}>
          <HandWaving size={30} weight="fill" />
        </div>
        <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight mb-3">Danke, wir haben Sie notiert!</h2>
        <p className="text-sm text-zinc-600 max-w-md mx-auto leading-relaxed">
          Sobald genügend Interessenten aus <strong>{address.city || `PLZ ${address.zip}`}</strong> zusammenkommen, planen wir eine Sondertour und melden uns bei Ihnen unter <strong>{state.form.email}</strong>.
        </p>
        <div className="mt-6 flex flex-wrap gap-3 justify-center">
          <Link to={`/portal/${tenant.slug}`}>
            <Button variant="outline" className="rounded-md" data-testid="interest-back-portal">Zur Startseite</Button>
          </Link>
          <Link to={`/portal/${tenant.slug}/karte`}>
            <Button className="rounded-md text-white" style={{ background: primary }} data-testid="interest-see-map">
              <MapPin size={14} weight="bold" className="mr-1.5" /> Interessenten-Karte
            </Button>
          </Link>
          <Link to={`/portal/${tenant.slug}/referenzen`}>
            <Button variant="outline" className="rounded-md" data-testid="interest-see-refs">Referenzen ansehen</Button>
          </Link>
        </div>
      </div>
    );
  }
  const reasonMsg = state.reason === "signup_stopped"
    ? `In ${address.zip} ${address.city} haben wir aktuell einen Aufnahmestopp${state.stop_reason ? ` (${state.stop_reason})` : ""}. Sie können sich vormerken lassen — wir melden uns, sobald wieder Plätze frei sind.`
    : state.reason === "out_of_area"
    ? `Ihre Adresse (${address.zip} ${address.city}) liegt aktuell außerhalb unseres Servicegebiets.`
    : "Ihre Adresse konnten wir nicht eindeutig zuordnen.";
  const reasonTitle = state.reason === "signup_stopped" ? "Momentan ausgebucht" : "Noch nicht in Ihrer Straße aktiv.";
  return (
    <div className="bg-white rounded-lg border border-black/5 p-8" data-testid="interest-panel">
      <div className="flex items-start gap-3 p-4 rounded-md mb-6 bg-amber-50 border border-amber-200">
        <Warning size={20} weight="fill" className="flex-shrink-0 mt-0.5 text-amber-600" />
        <div className="text-sm leading-relaxed">
          <strong className="text-amber-900">{reasonTitle}</strong>
          <div className="text-amber-800 mt-1">{reasonMsg}</div>
        </div>
      </div>
      <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight mb-2">
        Werden Sie Interessent
      </h2>
      <p className="text-sm text-zinc-600 mb-6 leading-relaxed">
        Tragen Sie sich ein — sobald genügend Anfragen aus <strong>{address.city || address.zip}</strong> vorliegen, planen wir eine Sondertour und informieren Sie zuerst.
      </p>
      <div className="grid md:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Vorname *</Label>
          <Input className="h-11 rounded-md" value={state.form.first_name} onChange={(e) => setState({ ...state, form: { ...state.form, first_name: e.target.value } })} data-testid="interest-first-name" />
        </div>
        <div>
          <Label className="text-xs">Nachname *</Label>
          <Input className="h-11 rounded-md" value={state.form.last_name} onChange={(e) => setState({ ...state, form: { ...state.form, last_name: e.target.value } })} data-testid="interest-last-name" />
        </div>
        <div>
          <Label className="text-xs">E-Mail *</Label>
          <Input type="email" className="h-11 rounded-md" placeholder="max@muster.de" value={state.form.email} onChange={(e) => setState({ ...state, form: { ...state.form, email: e.target.value } })} data-testid="interest-email" />
        </div>
        <div>
          <Label className="text-xs">Telefon (optional)</Label>
          <Input className="h-11 rounded-md" placeholder="+49 …" value={state.form.phone} onChange={(e) => setState({ ...state, form: { ...state.form, phone: e.target.value } })} data-testid="interest-phone" />
        </div>
      </div>
      <div className="mt-3">
        <Label className="text-xs">Anmerkung (optional)</Label>
        <textarea
          rows={3}
          className="w-full mt-1 border border-black/10 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black/10"
          placeholder="z.B. wir sind eine Nachbarschaft von 8 Häusern …"
          value={state.form.message}
          onChange={(e) => setState({ ...state, form: { ...state.form, message: e.target.value } })}
          data-testid="interest-message"
        />
      </div>
      <div className="mt-3 text-[11px] text-zinc-500 flex items-center gap-1.5">
        <MapPin size={12} weight="bold" /> Adresse: {address.street}, {address.zip} {address.city}
      </div>
      {state.error && <div className="mt-3 text-sm text-red-600" data-testid="interest-error">{state.error}</div>}
      <div className="mt-8 flex items-center justify-between">
        <Button variant="outline" className="rounded-md" onClick={onBack} data-testid="interest-back-btn"><ArrowLeft size={14} weight="bold" className="mr-1.5" /> Andere Adresse</Button>
        <Button
          onClick={onSubmit}
          disabled={state.submitting || !state.form.email || !state.form.first_name}
          className="rounded-md text-white h-12 px-7"
          style={{ background: primary }}
          data-testid="interest-submit-btn"
        >
          {state.submitting ? "Sende…" : "Als Interessent eintragen"}
          <EnvelopeSimple size={14} weight="bold" className="ml-1.5" />
        </Button>
      </div>
    </div>
  );
}
